/**
 * R4 — Society selection (design/redesign_v3/Auth & Onboarding.dc.html
 * frames "Society select" + "Onboarding desktop"). Debounced search +
 * "Near you" default list. Wired: GET /api/societies?q=. On pick, routes to
 * /onboarding/flat with societyId. Desktop (≥1024): steps rail + centered
 * content pane, matching the onboarding desktop shell.
 */
import { router } from 'expo-router';
import React from 'react';
import { View, useWindowDimensions } from 'react-native';

import { space } from '@bero/tokens';
import { extractItems, num, str } from '@/components/gate/api';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Field, Input, Pill, Screen, SectionLabel, Sheet, Skeleton, TopBar } from '@/ui';

import { DesktopShell, ONBOARDING_DESKTOP_BREAKPOINT, StepsRail } from './_shell';

type SocietyItem = {
  id: number; name: string; address: string | null; city: string | null;
  flatsCount: number | null; live: boolean;
};
type LoadState = 'loading' | 'ready' | 'error';

function parseSocieties(body: unknown): SocietyItem[] {
  return extractItems<unknown>(body, 'societies').map((raw, i) => ({
    id: num(raw, 'id') ?? i,
    name: str(raw, 'name') ?? 'Society',
    address: str(raw, 'address') ?? null,
    city: str(raw, 'city') ?? null,
    flatsCount: num(raw, 'flatsCount', 'flats', 'unitCount') ?? null,
    live: (str(raw, 'status') ?? 'LIVE') === 'LIVE',
  }));
}

function initialsOf(name: string): string {
  const words = name.split(' ').filter(Boolean);
  return (words[0]?.[0] ?? '') + (words[1]?.[0] ?? words[0]?.[1] ?? '');
}

/** Arch-shaped initials tile — brand rule: avatars/illustrations use the arch motif. */
function SocietyTile({ name }: { name: string }) {
  const { colors } = useTheme();
  return (
    <View style={{
      width: 44, height: 50, borderRadius: 6, borderTopLeftRadius: 22, borderTopRightRadius: 22,
      backgroundColor: colors.primary100, alignItems: 'center', justifyContent: 'center',
    }}>
      <AppText weight="600" style={{ color: colors.primary700, fontSize: 15 }}>{initialsOf(name).toUpperCase()}</AppText>
    </View>
  );
}

function SocietyRow({ item, onPress }: { item: SocietyItem; onPress: () => void }) {
  const { t } = useT();
  return (
    <Card onPress={onPress} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
      <SocietyTile name={item.name} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <AppText variant="headingSm" weight="500">{item.name}</AppText>
        {item.address || item.city ? (
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
            {item.address ?? item.city}
          </AppText>
        ) : null}
      </View>
      {!item.live ? (
        <Pill status="pending" label={t('onb_soc_not_on_bero')} />
      ) : item.flatsCount != null ? (
        <AppText variant="bodySm" tone="tertiary" mono>{t('onb_soc_flats', { count: item.flatsCount })}</AppText>
      ) : null}
    </Card>
  );
}

/** Lead-capture sheet — "my society isn't on Bero". This is the sales funnel:
 * every submit lands a LEAD row the team follows up on. */
function LeadSheet({ visible, onClose, prefill }: {
  visible: boolean;
  onClose: () => void;
  prefill: { societyId?: number; societyName?: string; city?: string };
}) {
  return (
    <Sheet visible={visible} onClose={onClose}>
      {/* mounted fresh per open → fields initialize from the prefill, no reset effect */}
      {visible ? <LeadForm prefill={prefill} onClose={onClose} /> : null}
    </Sheet>
  );
}

function LeadForm({ prefill, onClose }: {
  prefill: { societyId?: number; societyName?: string; city?: string };
  onClose: () => void;
}) {
  const { t } = useT();
  const [societyName, setSocietyName] = React.useState(prefill.societyName ?? '');
  const [city, setCity] = React.useState(prefill.city ?? '');
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [state, setState] = React.useState<'form' | 'sending' | 'done' | 'error'>('form');

  const canSend = societyName.trim().length > 1 && city.trim().length > 1 && phone.trim().length >= 10;

  const submit = async () => {
    if (!canSend || state === 'sending') return;
    setState('sending');
    try {
      await api.post<unknown>('/api/societies/lead', {
        societyId: prefill.societyId,
        societyName: societyName.trim(),
        city: city.trim(),
        name: name.trim(),
        phone: phone.trim(),
      });
      setState('done');
    } catch {
      setState('error');
    }
  };

  return (
    <>
      {state === 'done' ? (
        <View style={{ alignItems: 'center', gap: space.s3, paddingVertical: space.s4 }}>
          <AppText variant="headingMd" weight="600">{t('onb_lead_done_title')}</AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ textAlign: 'center', maxWidth: 300 }}>
            {t('onb_lead_done_body', { society: societyName.trim() || t('onb_lead_your_society') })}
          </AppText>
          <Button title={t('onb_done')} onPress={onClose} style={{ marginTop: space.s2, alignSelf: 'stretch' }} />
        </View>
      ) : (
        <>
          <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s1 }}>{t('onb_lead_title')}</AppText>
          <AppText variant="bodySm" tone="secondary" style={{ marginBottom: space.s4 }}>
            {t('onb_lead_sub')}
          </AppText>
          <View style={{ gap: space.s4 }}>
            <Field label={t('onb_lead_society_label')}>
              <Input value={societyName} onChangeText={setSocietyName} placeholder={t('onb_lead_society_ph')} />
            </Field>
            <Field label={t('onb_lead_city_label')}>
              <Input value={city} onChangeText={setCity} placeholder={t('onb_lead_city_ph')} />
            </Field>
            <Field label={t('onb_lead_name_label')}>
              <Input value={name} onChangeText={setName} placeholder={t('onb_lead_name_ph')} />
            </Field>
            <Field label={t('onb_lead_phone_label')}>
              <Input value={phone} onChangeText={setPhone} placeholder="98XXXXXX21" keyboardType="phone-pad" />
            </Field>
          </View>
          {state === 'error' ? (
            <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3 }}>
              {t('onb_lead_error')}
            </AppText>
          ) : null}
          <Button
            title={t('onb_lead_submit')}
            size="lg"
            loading={state === 'sending'}
            disabled={!canSend}
            onPress={() => void submit()}
            style={{ marginTop: space.s5 }}
          />
        </>
      )}
    </>
  );
}

function useSocietySearch() {
  const [query, setQuery] = React.useState('');
  const [societies, setSocieties] = React.useState<SocietyItem[]>([]);
  const [state, setState] = React.useState<LoadState>('loading');
  const [reloadTick, setReloadTick] = React.useState(0);
  const seq = React.useRef(0);

  React.useEffect(() => {
    const id = ++seq.current;
    void Promise.resolve().then(() => {
      if (id !== seq.current) return;
      setState('loading');
    });
    const t = setTimeout(async () => {
      try {
        const body = await api.get<unknown>(`/api/societies?q=${encodeURIComponent(query.trim())}`);
        if (id !== seq.current) return;
        setSocieties(parseSocieties(body));
        setState('ready');
      } catch {
        if (id !== seq.current) return;
        setSocieties([]);
        setState('error');
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, reloadTick]);

  return { query, setQuery, societies, state, retry: () => setReloadTick((n) => n + 1) };
}

function SocietyResults({ query, societies, state, retry, onLead }: {
  query: string; societies: SocietyItem[]; state: LoadState; retry: () => void;
  onLead: (s: SocietyItem) => void;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  if (state === 'loading') {
    // Capped at 2 — the demo/typical result set is 1-2 societies; a 3-item
    // skeleton implied a longer list than ever actually renders.
    return <View style={{ gap: space.s3 }}>{[0, 1].map((i) => <Skeleton key={i} h={78} />)}</View>;
  }
  if (state === 'error') {
    return (
      <EmptyState
        icon={<Icon name="building" size={40} color={colors.textTertiary} />}
        title={t('onb_soc_err_title')}
        body={t('onb_check_connection')}
        cta={t('retry')}
        onCta={retry}
      />
    );
  }
  if (societies.length === 0) {
    return (
      <EmptyState
        icon={<Icon name="search" size={40} color={colors.textTertiary} />}
        title={t('onb_soc_none_title')}
        body={t('onb_soc_none_body')}
      />
    );
  }
  return (
    <View style={{ gap: space.s3 }}>
      {societies.map((s) => (
        <SocietyRow
          key={s.id}
          item={s}
          onPress={() => {
            if (s.live) {
              router.push(`/onboarding/flat?societyId=${s.id}&societyName=${encodeURIComponent(s.name)}`);
            } else {
              onLead(s); // researched PROSPECT — capture the lead instead of a dead end
            }
          }}
        />
      ))}
    </View>
  );
}

export default function OnboardingSocietyScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { width } = useWindowDimensions();
  const { query, setQuery, societies, state, retry } = useSocietySearch();
  const [leadOpen, setLeadOpen] = React.useState(false);
  const [leadPrefill, setLeadPrefill] = React.useState<{ societyId?: number; societyName?: string; city?: string }>({});

  const openLead = (s?: SocietyItem) => {
    setLeadPrefill(s
      ? { societyId: s.id, societyName: s.name, city: s.city ?? '' }
      : { societyName: query.trim() });
    setLeadOpen(true);
  };

  const body = (
    <>
      <View style={{ position: 'relative', justifyContent: 'center' }}>
        <View style={{ position: 'absolute', left: 12, top: 0, bottom: 0, justifyContent: 'center', zIndex: 1 }}>
          <Icon name="search" size={16} color={colors.textTertiary} />
        </View>
        <Input
          value={query}
          onChangeText={setQuery}
          placeholder={t('onb_soc_search_ph')}
          autoCapitalize="none"
          returnKeyType="search"
          style={{ paddingLeft: 36 }}
        />
      </View>

      <SectionLabel style={{ marginTop: space.s4 }}>{query.trim() ? t('onb_results') : t('onb_near_you')}</SectionLabel>
      <SocietyResults query={query} societies={societies} state={state} retry={retry} onLead={openLead} />

      <AppText variant="bodySm" tone="secondary" style={{ marginTop: space.s4 }}>
        {t('onb_soc_cant_find')}{' '}
        <AppText
          variant="bodySm"
          weight="600"
          tone="brand"
          onPress={() => openLead()}
          accessibilityRole="button"
        >
          {t('onb_soc_ask_bring')}
        </AppText>
      </AppText>

      <LeadSheet visible={leadOpen} onClose={() => setLeadOpen(false)} prefill={leadPrefill} />
    </>
  );

  if (width >= ONBOARDING_DESKTOP_BREAKPOINT) {
    return (
      <DesktopShell
        rail={<StepsRail steps={[
          { label: t('onb_step_society'), sub: t('onb_step_society_sub'), state: 'active' },
          { label: t('onb_step_flat'), sub: t('onb_step_flat_sub'), state: 'upcoming' },
          { label: t('onb_step_verify'), sub: t('onb_step_verify_sub'), state: 'upcoming' },
        ]} />}
      >
        <View style={{ maxWidth: 560, width: '100%' }}>
          <AppText variant="displayLg">{t('onb_soc_title')}</AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
            {t('onb_soc_sub_desktop')}
          </AppText>
          <View style={{ marginTop: space.s6 }}>{body}</View>
        </View>
      </DesktopShell>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar title="" onBack={() => router.back()} bordered={false} />
      <Screen padded style={{ paddingTop: space.s2, gap: space.s4 }}>
        <View>
          <AppText variant="headingLg">{t('onb_soc_title')}</AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
            {t('onb_soc_sub_mobile')}
          </AppText>
        </View>
        {body}
      </Screen>
    </Screen>
  );
}
